import { FC, useMemo } from 'react'

import { StoreWithDeployStatus } from '@/models/operations/types'
import { AgGridApp } from '@/components'
import { classNames } from '@/utils'

import { buildOpsStoresColumnDefs } from '../data/columnsStores'

/** Below this count: virtual scroll only. Above: paginated pages (better for 100+ stores). */
const STORES_PAGINATION_THRESHOLD = 50
const STORES_DEFAULT_PAGE_SIZE = 50
const STORES_PAGE_SIZE_OPTIONS = [25, 50, 100, 200]

type StoresTableProps = {
  stores: StoreWithDeployStatus[]
  activeCount: number
  churnedCount: number
  status: 'active' | 'churned'
  search: string
  onStatusChange: (status: 'active' | 'churned') => void
  onSearchChange: (search: string) => void
  onRedeploy: (store: StoreWithDeployStatus) => void
  onClearCache: (store: StoreWithDeployStatus) => void
  actionLoadingId?: string | null
  interactionsDisabled?: boolean
}

type StoreTabProps = {
  label: string
  tooltip?: string
  count: number
  isActive: boolean
  countBg: string
  countClass: string
  onClick: () => void
}

const StoreTab: FC<StoreTabProps & { disabled?: boolean }> = ({
  label,
  tooltip,
  count,
  isActive,
  countBg,
  countClass,
  onClick,
  disabled = false,
}) => (
  <button
    type="button"
    disabled={disabled}
    title={tooltip}
    className={classNames(
      'flex items-center gap-2 px-4 py-2 border-b-2 -mb-px transition-colors disabled:opacity-40 disabled:cursor-not-allowed',
      isActive ? 'border-green-500' : 'border-transparent hover:border-gray-200',
    )}
    onClick={onClick}
  >
    <span
      className={classNames(
        'w-6 h-6 rounded-lg shrink-0 flex items-center justify-center text-xs font-bold leading-none',
        countBg,
        countClass,
      )}
    >
      {count}
    </span>
    <span
      className={classNames('text-sm font-medium', {
        'text-gray-800': isActive,
        'text-gray-500': !isActive,
      })}
    >
      {label}
    </span>
  </button>
)

const StoresTable: FC<StoresTableProps> = ({
  stores,
  activeCount,
  churnedCount,
  status,
  search,
  onStatusChange,
  onSearchChange,
  onRedeploy,
  onClearCache,
  actionLoadingId,
  interactionsDisabled = false,
}) => {
  const columnDefs = useMemo(
    () =>
      buildOpsStoresColumnDefs(
        onRedeploy,
        onClearCache,
        actionLoadingId,
        interactionsDisabled || !!actionLoadingId,
      ),
    [onRedeploy, onClearCache, actionLoadingId, interactionsDisabled],
  )

  const usePagination = stores.length > STORES_PAGINATION_THRESHOLD

  return (
    <div className="flex flex-col gap-4 w-full min-h-0 flex-1 lg:h-full">
      <div className="flex border-b border-gray-200 shrink-0">
        <StoreTab
          label="Active"
          tooltip="Active stores"
          count={activeCount}
          isActive={status === 'active'}
          countBg="bg-green-50"
          countClass="text-green-500"
          disabled={interactionsDisabled}
          onClick={() => onStatusChange('active')}
        />
        <StoreTab
          label="Churned"
          tooltip="Inactive stores"
          count={churnedCount}
          isActive={status === 'churned'}
          countBg="bg-red-50"
          countClass="text-red"
          disabled={interactionsDisabled}
          onClick={() => onStatusChange('churned')}
        />
      </div>

      <input
        type="search"
        placeholder="Search by store name..."
        value={search}
        disabled={interactionsDisabled}
        onChange={(e) => onSearchChange(e.target.value)}
        className="w-full md:w-1/2 lg:w-[30%] shrink-0 border-[1px] rounded-[6px] text-sm h-[40px] px-3 text-gray-700 placeholder:text-gray-400 border-gray-200 focus:border-gray-300 outline-none disabled:opacity-40 disabled:cursor-not-allowed"
      />

      <div className="flex flex-col w-full min-h-[280px] lg:flex-1 lg:min-h-0">
        <AgGridApp
          key={`stores-grid-${interactionsDisabled ? 'locked' : 'idle'}-${actionLoadingId ?? ''}-${usePagination ? 'paged' : 'scroll'}`}
          className="ops-ag-grid"
          fillHeight
          rowData={stores}
          columnDefs={columnDefs}
          isShowFilter={false}
          isShowPagination={usePagination}
          paginationPageSize={STORES_DEFAULT_PAGE_SIZE}
          paginationPageSizeSelector={STORES_PAGE_SIZE_OPTIONS}
          rowBuffer={20}
          debounceVerticalScrollbar
          minHeight={280}
        />
      </div>
    </div>
  )
}

export { StoresTable }
